import {
    infobarMessageForSMIMENotSupported,
    infobarWarningSmimeNotSupportedForAccount,
    actionTextForSMIMENotSupported,
} from './createSMIMEInfoBarMessage.locstring.json';
import loc from 'owa-localize';
import { logUsage } from 'owa-analytics';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getConversationReadingPaneViewState';
import popoutReadingPane from 'owa-popout-utils/lib/utils/popoutReadingPane';
import Message from 'owa-service/lib/contract/Message';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import SmimeContext from 'owa-smime-types/lib/schema/SmimeContext';
import getSmimeAttachmentType from 'owa-smime/lib/utils/getSmimeAttachmentType';
import getSmimeControlErrorInfobarMessage from 'owa-smime/lib/utils/getSmimeControlErrorInfobarMessage';
import isClearSignedItem from 'owa-smime/lib/utils/isClearSignedItem';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import {
    InfoBarCustomAction,
    InfoBarMessageColor,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

const createSmimeConversationInfoBarViewState = (
    key: string,
    openNewWindowAction: InfoBarCustomAction
): InfoBarMessageViewState => ({
    key: key,
    source: InfoBarMessageSource.ReadingPane,
    rank: InfoBarMessageRank.Info,
    color: InfoBarMessageColor.Grey,
    message: loc(infobarMessageForSMIMENotSupported),
    messageParts: [loc(infobarMessageForSMIMENotSupported), openNewWindowAction],
});

export default function tryCreateSMIMEInfoBarMessage(
    message: Message,
    key: string,
    isConversationItemPart: boolean
): InfoBarMessageViewState {
    if (!isSMIMEItem(message)) {
        return null;
    } else if (isConversationItemPart) {
        // S/MIME is not supported in conversation view
        // Show infobar to open in popup
        return createSmimeConversationInfoBarViewState(key, {
            action: () => {
                logUsage(
                    'RPConversationViewSmimeItemPopoutClick',
                    {
                        numItems: getConversationReadingPaneViewState(message.ConversationId?.Id)
                            .itemPartsMap.size,
                    },
                    { isCore: true }
                );
                popoutReadingPane(message.ItemId.Id);
            },
            text: loc(actionTextForSMIMENotSupported),
        });
    } else if (isConsumer() && !isClearSignedItem(message)) {
        // Consumers cannot read encrypted mails.
        // Show infobar that S/MIME is not supported for consumer accounts.
        return {
            key,
            rank: InfoBarMessageRank.Error,
            source: InfoBarMessageSource.ReadingPane,
            message: infobarWarningSmimeNotSupportedForAccount,
            messageParts: [loc(infobarWarningSmimeNotSupportedForAccount)],
        };
    } else {
        /**
         * This condition is used to determine whether item is
         * clear/opaque signed. For a S/MIME item, getSmimeAttachmentType()
         * will be false when item is decoded on server(we don't have p7m file attachment in that case).
         * Server can only decode clear/opaque S/MIME messages
         */
        const isItemSigned = isClearSignedItem(message) || !getSmimeAttachmentType(message);
        const context = isItemSigned
            ? SmimeContext.ReadingSignedMessage
            : SmimeContext.ReadingEncryptedMessage;
        return getSmimeControlErrorInfobarMessage(
            key,
            context,
            isItemSigned,
            false /* isCompose*/,
            false /* isReply */,
            false /* isForward */
        );
    }

    return null;
}
